import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const reputationItems = [
  {
    icon: 'ri-award-line',
    title: '学术严谨性',
    description: '所有内容基于公开学术文献、法律文件和电影史料编纂，由电影研究学者和资深影评人联合撰写，确保信息准确性与分析深度。',
  },
  {
    icon: 'ri-team-line',
    title: '专家团队',
    description: '编撰团队包括香港及亚洲电影研究领域的专家学者，平均从业经验超过15年，具备电影学、文化研究、法律等多学科背景。',
  },
  {
    icon: 'ri-book-open-line',
    title: '丰富资源',
    description: '网站收录超过200篇深度文章，覆盖电影分级制度、审查法律、代表作品分析和文化研究等多个领域，持续更新中。',
  },
  {
    icon: 'ri-global-line',
    title: '国际视野',
    description: '研究范围涵盖香港、台湾、日本、韩国、中国大陆及欧美等地区的电影分级制度，提供跨国比较的学术视角。',
  },
  {
    icon: 'ri-shield-star-line',
    title: '客观中立',
    description: '坚持学术中立立场，不涉及政治立场表达，专注于电影分级制度本身的历史、法律和文化维度的客观分析。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '读者信赖',
    description: '自创办以来，已服务超过50万电影研究者和爱好者，文章被多所高校的电影课程引为参考资料，建立了广泛的专业信誉。',
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u4g953pcjqs2fcsm00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <section className="py-10 md:py-14">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                关于电影分级研究
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
                电影分级研究是一个专注于香港及亚洲电影分级制度的学术知识平台。我们致力于系统梳理三级片的分级制度起源、历史演变与文化背景，为电影研究者、文化学者和电影爱好者提供权威、客观、全面的学术资源。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
              <div>
                <h2 className="text-xl font-semibold text-foreground-950 mb-4">使命与愿景</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  在信息碎片化的时代，关于三级片的讨论常常流于表面，缺乏系统性和学术性。我们的使命是填补这一空白——通过严谨的学术研究、深入的文化分析和客观的历史梳理，帮助公众建立对电影分级制度的正确认知。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  我们相信，电影分级制度不仅是一个法律框架，更是一面反映社会价值观、文化传统和时代精神的多棱镜。通过研究三级片的分级制度，我们可以更深入地理解香港及亚洲社会的文化演变历程。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们期待成为华语世界最权威的电影分级制度研究平台，为学术研究、电影教育和公众文化素养的提升贡献力量。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=vintage%20film%20research%20library%20with%20warm%20lighting%2C%20wooden%20bookshelves%2C%20film%20reels%2C%20classic%20movie%20posters%2C%20academic%20atmosphere%2C%20scholarly%20books%2C%20elegant%20study%2C%20soft%20golden%20light&width=800&height=500&seq=about-film-research&orientation=landscape"
                  alt="电影分级研究学术图书馆"
                  className="w-full h-full object-cover"
                  title="电影分级研究学术图书馆"
                />
              </div>
            </div>
          </section>

          <section id="reputation" className="py-10 md:py-14 border-t border-background-200/70">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
                学术声誉
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                我们坚持学术严谨与客观中立，赢得了专业领域的广泛认可
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reputationItems.map((item) => (
                <div key={item.title} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="w-12 h-12 bg-primary-100/70 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="py-10 md:py-14 border-t border-background-200/70">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  如有任何问题、建议或学术合作意向，欢迎与我们取得联系
                </p>
              </div>

              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    消息 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的消息（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多可输入500个字符</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-honeypot"
                />

                <button
                  type="submit"
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送消息
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-700">消息已成功发送，我们将尽快回复您！</p>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-700">{formError}</p>
                  </div>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}